<template>
  <b-modal
    :model-value="open"
    :title="$t('expert_comparison.modal_title')"
    size="5xl"
    full-scroll
    no-footer
    @update:model-value="(v: boolean) => { if (!v) $emit('close'); }"
  >
    <div class="px-6 pb-6 pt-1">
      <div v-if="loading" class="py-16 flex justify-center" data-e2e="compare-loading">
        <b-loader-spinner />
      </div>

      <div v-else-if="error" class="py-16 text-center" data-e2e="compare-error">
        <p class="text-gray-300 mb-4">{{ $t('expert_comparison.load_error') }}</p>
        <button class="bg-primary hover:bg-primary-bold transition-colors text-white px-4 py-2 rounded-lg cursor-pointer" @click="$emit('retry')">
          {{ $t('expert_comparison.retry') }}
        </button>
      </div>

      <div v-else-if="experts.length >= 2" class="space-y-8" data-e2e="compare-content">
        <!-- Identity headers : grille alignée par lignes partagées (subgrid).
             Chaque carte occupe les 7 mêmes lignes (identité, poste, localisation,
             score, BIG Score, résumé, pitch) : une info donnée est toujours sur la
             même ligne horizontale d'une colonne à l'autre, l'emplacement est réservé
             même si le profil ne l'a pas renseigné. `minmax(240px, 1fr)` : les colonnes
             remplissent la largeur à 2-3 experts et scrollent horizontalement à 4-5. -->
        <div
          class="grid gap-x-4 gap-y-2 overflow-x-auto scrollbar-custom pb-1"
          :style="{ gridTemplateColumns: `repeat(${experts.length}, minmax(240px, 1fr))`, gridTemplateRows: 'repeat(7, auto)' }"
        >
          <div
            v-for="(expert, i) in experts"
            :key="expert.id"
            class="grid grid-rows-subgrid rounded-xl bg-gray-900 border border-t-4 border-[#373737] p-4 transition-colors hover:border-gray-600"
            :style="{ gridRow: 'span 7', borderTopColor: seriesColors[i] }"
          >
            <!-- Ligne 1 : avatar + nom -->
            <div class="flex items-center gap-3 min-w-0">
              <div class="shrink-0 rounded-full p-[2px]" :style="{ background: seriesColors[i] }">
                <b-avatar
                  class="block"
                  size="lg"
                  :url="expert.signed_picture_url || ''"
                  :initials="getInitials(expert)"
                  :alt="`${expert.first_name} ${expert.last_name}`"
                />
              </div>
              <div class="min-w-0 flex-1">
                <a
                  v-if="expert.handle"
                  :href="`${appUrl}account/${expert.handle}`"
                  target="_blank"
                  rel="noopener"
                  class="group inline-flex items-center gap-1 max-w-full text-white font-semibold hover:text-primary transition-colors"
                >
                  <span class="truncate">{{ expert.first_name }} {{ expert.last_name }}</span>
                  <Icon name="solar:square-arrow-right-up-linear" class="size-3.5 shrink-0 text-gray-400 group-hover:text-primary transition-colors" />
                </a>
                <p v-else class="text-white font-semibold truncate">{{ expert.first_name }} {{ expert.last_name }}</p>
              </div>
            </div>

            <!-- Ligne 2 : poste -->
            <p class="text-gray-400 text-xs truncate">{{ expert.job_position }}</p>

            <!-- Ligne 3 : localisation + langues parlées (#271) -->
            <div class="flex flex-wrap items-center gap-2 text-gray-500 text-xs">
              <span v-if="expert.city" class="inline-flex items-center gap-1 max-w-full">
                <Icon name="solar:map-point-outline" class="size-3.5 shrink-0" />
                <span class="truncate">{{ expert.city }}</span>
              </span>
              <div v-if="orderedLanguages(expert).length" class="flex flex-wrap gap-1">
                <b-badge v-for="code in orderedLanguages(expert)" :key="code" pill variant="skill">{{ $t('languages.' + code) }}</b-badge>
              </div>
            </div>

            <!-- Ligne 4 : score de matching (+ aide) -->
            <div class="flex items-center gap-1.5">
              <span
                v-if="expert.matching_score !== null"
                class="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
                :class="scoreClasses(expert.matching_score)"
              >
                {{ $t('expert_comparison.score', { score: Math.round(expert.matching_score) }) }}
              </span>
              <!-- Aide : distinction Score de matching mission vs BIG Score plateforme.
                   Style harmonisé avec BScoreTooltip.vue (fond #2a2a2b, bordure #515154, titre + liste à icônes). -->
              <span
                v-if="expert.matching_score !== null || expert.big_score !== null"
                class="relative inline-flex items-center group/tip"
              >
                <button
                  type="button"
                  class="-m-1.5 inline-flex items-center p-1.5 text-gray-400 hover:text-white focus-visible:text-white cursor-help transition-colors focus:outline-none"
                  :aria-label="$t('expert_comparison.score_tooltip_title')"
                >
                  <Icon name="solar:info-circle-linear" class="size-4" />
                </button>
                <div
                  role="tooltip"
                  class="pointer-events-none absolute bottom-full left-1/2 mb-2 w-80 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-[#515154] bg-[#2a2a2b] p-3 opacity-0 shadow-xl transition-opacity duration-150 z-40 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
                >
                  <p class="mb-2 text-xs font-medium text-[#9d9d9d]">{{ $t('expert_comparison.score_tooltip_title') }}</p>
                  <ul class="space-y-2">
                    <li class="flex items-start gap-2">
                      <Icon name="solar:link-bold" class="size-4 shrink-0 mt-0.5 text-gray-300" />
                      <span class="text-xs leading-relaxed text-white/90">
                        <span class="font-semibold text-white">{{ $t('expert_comparison.score_tooltip_matching_label') }}</span>
                        <span class="text-gray-500"> · </span>{{ $t('expert_comparison.score_tooltip_matching_desc') }}
                      </span>
                    </li>
                    <li class="flex items-start gap-2">
                      <Icon name="solar:medal-ribbons-star-bold-duotone" class="size-4 shrink-0 mt-0.5 text-primary" />
                      <span class="text-xs leading-relaxed text-white/90">
                        <span class="font-semibold text-white">{{ $t('expert_comparison.score_tooltip_big_label') }}</span>
                        <span class="text-gray-500"> · </span>{{ $t('expert_comparison.score_tooltip_big_desc') }}
                      </span>
                    </li>
                  </ul>
                </div>
              </span>
            </div>

            <!-- Ligne 5 : BIG Score + thématique dominante.
                 La thématique (skill du top_context) indique SUR QUELLE spécialité
                 porte le score, pour ne pas comparer deux Big Scores hétérogènes
                 comme s'ils l'étaient (ticket #195). -->
            <div v-if="expert.big_score !== null" class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span
                class="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary whitespace-nowrap"
                :title="$t('expert_comparison.big_score')"
              >
                {{ $t('expert_comparison.big_score') }} · {{ expert.big_score }}
              </span>
              <span v-if="expert.big_score_specialty" class="text-gray-400 text-xs truncate">
                — {{ expert.big_score_specialty }}
              </span>
            </div>
            <div v-else />

            <!-- Ligne 6 : résumé -->
            <p class="text-gray-400 text-xs leading-relaxed line-clamp-3">{{ expert.summary }}</p>

            <!-- Ligne 7 : pitch vidéo -->
            <div>
              <a
                v-if="expert.signed_video_url"
                :href="expert.signed_video_url"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-1.5 rounded-lg border border-[#373737] bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-gray-500 hover:text-white transition-colors"
              >
                <Icon name="solar:play-circle-bold" class="size-4 text-primary" />
                {{ $t('expert_comparison.watch_pitch') }}
              </a>
            </div>
          </div>
        </div>

        <!-- Radars -->
        <div>
          <h3 class="flex items-center gap-2 text-white text-sm font-semibold uppercase tracking-wide mb-3">
            <span class="inline-block w-1 h-4 rounded-full bg-primary" />
            {{ $t('expert_comparison.skills') }}
          </h3>
          <!-- Horizontal scroll (comme le profil) : chaque radar a assez de place pour
               afficher ses labels sans coupure et reste centré dans sa carte. -->
          <div class="relative">
            <button
              v-if="canScrollLeft"
              class="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center h-8 w-8 rounded-full bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer shadow-lg"
              @click="scrollBy(-1)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              v-if="canScrollRight"
              class="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center h-8 w-8 rounded-full bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer shadow-lg"
              @click="scrollBy(1)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            <div ref="scrollContainer" class="flex gap-3 overflow-x-auto scroll-smooth snap-x pb-1 scrollbar-custom" @scroll="updateScrollState">
              <div
                v-for="dim in radarDimensions"
                :key="dim.key"
                class="shrink-0 w-[320px] snap-start rounded-xl bg-gray-900 border border-[#373737] p-3 transition-colors hover:border-gray-600"
              >
                <h4 class="mb-1 text-gray-300 text-xs font-medium uppercase tracking-wide text-center">{{ $t(dim.labelKey) }}</h4>
                <b-radar-chart v-if="dim.hasData" :series="dim.series" :height="280" />
                <p v-else class="text-gray-400 text-sm text-center py-8">{{ $t('expert_comparison.no_radar_data') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Experiences -->
        <div>
          <h3 class="flex items-center gap-2 text-white text-sm font-semibold uppercase tracking-wide mb-3">
            <span class="inline-block w-1 h-4 rounded-full bg-primary" />
            {{ $t('expert_comparison.experiences') }}
          </h3>
          <!-- Hauteur limitée (~4 expériences), scroll interne pour faire défiler. -->
          <div class="flex gap-4 overflow-x-auto pb-1 scrollbar-custom">
            <div v-for="(expert, i) in experts" :key="expert.id" class="shrink-0 w-full sm:w-[240px] space-y-2 self-start max-h-[360px] overflow-y-auto pr-1 scrollbar-custom">
              <!-- En-tête de colonne (sticky) : à qui appartiennent ces expériences -->
              <div class="sticky top-0 z-10 flex items-center gap-2 bg-gray-800 py-1">
                <span class="inline-block w-2.5 h-2.5 rounded-full shrink-0" :style="{ background: seriesColors[i] }" />
                <span class="text-gray-300 text-xs font-semibold truncate">{{ expert.first_name }}</span>
                <span class="text-gray-600 text-xs">· {{ expert.experiences.length }}</span>
              </div>
              <div
                v-for="exp in expert.experiences"
                :key="exp.id"
                class="rounded-xl bg-gray-900 border border-[#373737] p-3 transition-colors hover:border-gray-600"
              >
                <p class="text-white text-sm font-medium leading-snug">{{ exp.title || exp.company_name }}</p>
                <p v-if="exp.company_name && exp.title" class="text-gray-400 text-xs mt-0.5 truncate">{{ exp.company_name }}</p>
                <p class="text-gray-500 text-xs mt-1.5 inline-flex items-center gap-1">
                  <Icon name="solar:calendar-outline" class="size-3.5 shrink-0" />
                  {{ formatPeriod(exp) }}
                </p>
              </div>
              <p v-if="expert.experiences.length === 0" class="text-gray-500 text-sm italic py-2">
                {{ $t('expert_comparison.no_experiences') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Contextual CTA (e.g. add to favorites on the search page) -->
        <div v-if="contextCtaLabel" class="flex items-center justify-end pt-2 border-t border-[#373737]">
          <button
            class="bg-primary hover:bg-primary-bold transition-colors text-white text-sm font-medium px-4 py-2 rounded-lg cursor-pointer"
            data-e2e="compare-context-cta"
            @click="$emit('context-action', experts.map((e) => e.id))"
          >
            {{ contextCtaLabel }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { radarDepthValue } from '~/help/radarDepth';
import { useRadarTooltip } from '~/composables/useRadarTooltip';
import BModal from '~/components/modal/BModal.vue';
import BRadarChart from '~/components/graph/BRadarChart.vue';
import BLoaderSpinner from '~/components/loader/BLoaderSpinner.vue';
import BAvatar from '~/components/avatar/BAvatar.vue';
import BBadge from '~/components/badge/BBadge.vue';
import { orderedSpokenLanguages } from '~/help/languages';
import type { CompareExpert, CompareExperience, CompareExpertsResponse } from '~/api/v2/users/UserRepository';
import type { CompareContext } from '~/composables/useExpertComparison';

const props = defineProps<{
  open: boolean;
  loading: boolean;
  error: string | null;
  data: CompareExpertsResponse | null;
  context: CompareContext;
}>();

defineEmits<{
  (e: 'close'): void;
  (e: 'retry'): void;
  (e: 'context-action', ids: string[]): void;
}>();

const { t } = useI18n();
const radarTooltip = useRadarTooltip();
const config = useRuntimeConfig();
const appUrl = computed(() => config.public.NUXT_APP_URL ?? '/');

// Ordre = index de l'expert dans la short-list (jusqu'à 5). Le front assigne la
// couleur par index (identité + série radar).
const seriesColors = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];

const experts = computed<CompareExpert[]>(() => props.data?.experts ?? []);

// Horizontal scroll state for the radar row (arrows, like the profile).
const scrollContainer = ref<HTMLDivElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const updateScrollState = () => {
  const el = scrollContainer.value;
  if (!el) {
    canScrollLeft.value = false;
    canScrollRight.value = false;
    return;
  }
  canScrollLeft.value = el.scrollLeft > 4;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
};

const scrollBy = (direction: -1 | 1) => {
  const el = scrollContainer.value;
  if (!el) return;
  el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
};

watch([experts, () => props.open], async () => {
  await nextTick();
  updateScrollState();
});

onMounted(() => window.addEventListener('resize', updateScrollState));
onBeforeUnmount(() => window.removeEventListener('resize', updateScrollState));

const radarDimensions = computed(() => {
  const dims = [
    { key: 'industries', labelKey: 'expert_comparison.industry' },
    { key: 'market_segments', labelKey: 'expert_comparison.market_segment' },
    { key: 'company_sizes', labelKey: 'expert_comparison.company_type' },
    { key: 'deal_sizes', labelKey: 'expert_comparison.deal_sizes' },
  ] as const;
  return dims.map((d) => {
    const series = experts.value.map((expert, i) => ({
      name: expert.first_name,
      color: seriesColors[i],
      data: (expert.radars[d.key] ?? []).map((p) => ({
        label: p.label,
        value: radarDepthValue(p),
        tooltip: radarTooltip(p),
      })),
    }));
    const hasData = series.some((s) => s.data.length > 0);
    return { key: d.key, labelKey: d.labelKey, series, hasData };
  });
});

const contextCtaLabel = computed(() => {
  switch (props.context) {
    case 'search':
    case 'search-mission':
      // Sur la recherche, les experts ne sont pas encore favoris : CTA utile.
      return t('expert_comparison.cta_favorite');
    // Dans les étapes de création, les 2 experts sont déjà sélectionnés avant
    // d'ouvrir la comparaison : pas de CTA (la modale est une aide à la décision).
    case 'mission-wizard':
    case 'passd':
    default:
      return '';
  }
});

function orderedLanguages(expert: CompareExpert) {
  return orderedSpokenLanguages(expert.languages);
}

function getInitials(expert: CompareExpert): string {
  return `${expert.first_name?.[0] ?? ''}${expert.last_name?.[0] ?? ''}`.toUpperCase();
}

// Pastille de score colorée selon la valeur (cohérent avec les barres de matching ailleurs).
function scoreClasses(score: number): string {
  if (score >= 80) return 'bg-green-500/15 text-green-400 border-green-500/30';
  if (score >= 40) return 'bg-orange-500/15 text-orange-400 border-orange-500/30';
  return 'bg-red-500/15 text-red-400 border-red-500/30';
}

function formatPeriod(exp: CompareExperience): string {
  const start = exp.start_at ? exp.start_at.slice(0, 7) : '';
  const end = exp.is_ongoing ? t('expert_comparison.present') : exp.end_at ? exp.end_at.slice(0, 7) : '';
  return [start, end].filter(Boolean).join(' — ');
}
</script>
